/**
 * Modal para Editar Prazos
 * Permite editar data de vencimento, observações e outras informações do prazo
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Clock, FileText, Save, X, AlertTriangle } from 'lucide-react';

const EditarPrazoModal = ({ 
  isOpen, 
  onClose, 
  prazo, 
  onSalvar,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    dataVencimento: '',
    observacoes: '',
    tipo: '',
    descricao: '',
    dias: 0
  });

  const [errors, setErrors] = useState({});

  // Carregar dados do prazo quando modal abrir
  useEffect(() => {
    if (isOpen && prazo) {
      setFormData({
        dataVencimento: prazo.dataVencimento || '',
        observacoes: prazo.observacoes || '',
        tipo: prazo.tipo || '',
        descricao: prazo.descricao || '',
        dias: prazo.dias || 0
      });
      setErrors({});
    }
  }, [isOpen, prazo]);

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    if (!formData.dataVencimento) {
      newErrors.dataVencimento = 'Data de vencimento é obrigatória';
    }

    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo do prazo é obrigatório';
    }

    if (formData.dias < 0) {
      newErrors.dias = 'Dias não pode ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calcular dias restantes
  const calcularDiasRestantes = (dataVencimento) => {
    if (!dataVencimento) return 0;
    
    const hoje = new Date();
    const vencimento = new Date(dataVencimento);
    return Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
  };

  // Calcular dias totais do prazo
  const calcularDiasTotais = (dataInicio, dataVencimento) => {
    if (!dataInicio || !dataVencimento) return 0;
    
    const inicio = new Date(dataInicio);
    const vencimento = new Date(dataVencimento);
    return Math.ceil((vencimento - inicio) / (1000 * 60 * 60 * 24));
  };

  // Atualizar campo
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Recalcular dias se mudou a data de vencimento
    if (field === 'dataVencimento' && prazo?.dataInicio) {
      const diasTotais = calcularDiasTotais(prazo.dataInicio, value);
      setFormData(prev => ({
        ...prev,
        dias: diasTotais
      }));
    }
  };

  // Salvar alterações
  const handleSalvar = async () => {
    if (!validateForm()) return;

    const diasRestantes = calcularDiasRestantes(formData.dataVencimento);
    
    // Determinar urgência
    let urgencia = 'proximo_mes';
    if (diasRestantes < 0) urgencia = 'vencido';
    else if (diasRestantes === 0) urgencia = 'hoje';
    else if (diasRestantes <= 7) urgencia = 'esta_semana';

    const prazoAtualizado = {
      ...prazo,
      ...formData,
      diasRestantes,
      urgencia,
      dataEdicao: new Date().toISOString()
    };

    await onSalvar(prazoAtualizado);
  };

  if (!isOpen) return null;

  const diasRestantes = calcularDiasRestantes(formData.dataVencimento);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Editar Prazo
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-6 space-y-4">
          {/* Informações do processo */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-1">
              {prazo?.numeroProcesso}
            </h3>
            <p className="text-sm text-gray-600">
              {prazo?.cliente} • {prazo?.tribunal}
            </p>
          </div>

          {/* Tipo do prazo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo do Prazo
            </label>
            <input
              type="text"
              value={formData.tipo}
              onChange={(e) => handleChange('tipo', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.tipo ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: contestação, recurso, manifestação"
            />
            {errors.tipo && (
              <p className="text-red-600 text-xs mt-1">{errors.tipo}</p>
            )}
          </div>

          {/* Data de vencimento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Vencimento
            </label>
            <input
              type="date"
              value={formData.dataVencimento}
              onChange={(e) => handleChange('dataVencimento', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.dataVencimento ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.dataVencimento && (
              <p className="text-red-600 text-xs mt-1">{errors.dataVencimento}</p>
            )}
            
            {/* Indicador de dias restantes */}
            {formData.dataVencimento && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Clock size={14} className="text-gray-400" />
                <span className={`font-medium ${
                  diasRestantes < 0 ? 'text-red-600' : 
                  diasRestantes === 0 ? 'text-orange-600' : 
                  diasRestantes <= 7 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {diasRestantes < 0 
                    ? `${Math.abs(diasRestantes)} dias em atraso`
                    : diasRestantes === 0 
                    ? 'Vence hoje'
                    : `${diasRestantes} dias restantes`
                  }
                </span>
                {diasRestantes < 0 && (
                  <AlertTriangle size={14} className="text-red-500" />
                )}
              </div>
            )}
          </div>

          {/* Dias do prazo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prazo Total (dias)
            </label>
            <input
              type="number"
              min="0"
              value={formData.dias}
              onChange={(e) => handleChange('dias', parseInt(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.dias ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: 15"
            />
            {errors.dias && (
              <p className="text-red-600 text-xs mt-1">{errors.dias}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Prazo total em dias (ex: 15 dias para contestar)
            </p>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              value={formData.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrição do que deve ser feito..."
            />
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações Pessoais
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleChange('observacoes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Suas anotações sobre este prazo..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Observações pessoais que só você verá
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Save size={16} />
            )}
            Salvar Alterações
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditarPrazoModal;
